import * as React from 'react';
import { observer } from 'src/utils/mobx-react';
import { TabLabel, TabWrapper } from 'src/domains/layouts/webview/components/debugPanel/DebugPanel.style';
import { action, computed, makeObservable, observable } from 'mobx';
import { assertNever } from 'src_common/common/assertNever';
import { RTLPragmatic } from './MainViewCasino/rtl-pragmatic/RTLPragmatic';
import { GamesInspector } from './MainViewCasino/games-inspector/GamesInspector';
import { RTLDebug } from './MainViewCasino/rtl/RTLDebug';
import { CollectionType } from 'src/domains/casino/state/AppCasino.state';
import { Common, useCommon } from 'src/domains/common/Common';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';

type CasinoTabIdType = 'games_inspector' | 'rtl' | 'rtl_pragmatic';
type CasinoTabType = {
    id: CasinoTabIdType;
    label: string;
};

class NavigationState {
    @observable private selectedTabId: CasinoTabIdType | null = null;
    @observable public selectedWebsiteCollection: CollectionType | null = null;
    @observable public selectedWebsiteTabId: string | null = null;

    public constructor(private readonly common: Common) {
        makeObservable(this);
        const starRouter = StarRouter.get(this.common);

        starRouter.onChangeCurrentView((prevCurrentView, nextCurrentView) => {
            if (nextCurrentView?.name === 'casino' || nextCurrentView?.name === 'live-casino') {
                this.selectWebsiteCollection(nextCurrentView.name);
                this.selectWebsiteTabId(nextCurrentView.tabId ?? null);
            } else if (nextCurrentView?.name === 'virtuals') {
                this.selectWebsiteCollection(nextCurrentView.name);
                this.selectWebsiteTabId(null);
            } else {
                this.selectWebsiteCollection(null);
                this.selectWebsiteTabId(null);
            }
            console.log('debugPrevView: ', prevCurrentView, 'debugNextView: ', nextCurrentView);
        });
    }

    @computed public get tabList(): Array<CasinoTabType> {
        return [
            {
                id: 'games_inspector',
                label: 'Games Inspector',
            },
            {
                id: 'rtl',
                label: 'RTL',
            },
            {
                id: 'rtl_pragmatic',
                label: 'RTL Pragmatic',
            },
        ];
    }

    @computed private get firstTabId(): CasinoTabIdType | null {
        if (this.tabList[0] === undefined) {
            return null;
        }
        return this.tabList[0].id;
    }

    @computed public get activeTabId(): CasinoTabIdType | null {
        return this.selectedTabId ?? this.firstTabId;
    }

    @action public selectTab = (tabId: CasinoTabIdType): void => {
        this.selectedTabId = tabId;
    };

    @action public selectWebsiteCollection = (websiteCollection: CollectionType | null): void => {
        this.selectedWebsiteCollection = websiteCollection;
    };

    @action public selectWebsiteTabId = (websiteTabId: string | null): void => {
        this.selectedWebsiteTabId = websiteTabId;
    };
}

const renderTabContent = (navigationState: NavigationState): React.ReactNode => {
    const { activeTabId } = navigationState;

    if (activeTabId === null) {
        return null;
    }

    if (activeTabId === 'games_inspector') {
        return (
            <GamesInspector
                collection={navigationState.selectedWebsiteCollection}
                tabId={navigationState.selectedWebsiteTabId}
            />
        );
    }

    if (activeTabId === 'rtl') {
        return (
            <RTLDebug
                collection={navigationState.selectedWebsiteCollection}
                tabId={navigationState.selectedWebsiteTabId}
            />
        );
    }

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (activeTabId === 'rtl_pragmatic') {
        return <RTLPragmatic></RTLPragmatic>;
    }

    assertNever('Debug Panel Tab Id ', activeTabId);
};

interface CasinoTabPropsType {
    navigationState: NavigationState;
    id: CasinoTabIdType;
    label: string;
}

const CasinoTab = observer('CasinoTab', (props: CasinoTabPropsType) => {
    const handleTabClick = (): void => {
        props.navigationState.selectTab(props.id);
    };

    const isActive = props.navigationState.activeTabId === props.id;

    return (
        <TabLabel
            active={isActive}
            onClick={handleTabClick}
        >
            {props.label}
        </TabLabel>
    );
});

export const MainViewCasino = observer('MainViewLiveCasino', () => {
    const common = useCommon();
    const [navigationState] = React.useState(() => new NavigationState(common));

    return (
        <>
            <TabWrapper>
                {navigationState.tabList.map((tab) => (
                    <CasinoTab
                        key={tab.id}
                        navigationState={navigationState}
                        id={tab.id}
                        label={tab.label}
                    ></CasinoTab>
                ))}
            </TabWrapper>

            {renderTabContent(navigationState)}
        </>
    );
});
